import React from 'react';
import { merge } from 'merge-anything';
import { ThemeProvider, PortalProvider, Mapbox } from './ui';
import { createTheme, theme as baseTheme } from './ui/system';
import { AppRoutes } from './routing';
import { useAuthService, ApiProvider } from './hooks';
import { ProvideAppState } from './hooks/useAppState';
import { Site } from './components';
import defaultMarkerImage from './res/mapMarker.png';
import { ConfirmProvider } from './components/confirm/ConfirmProvider';
import { Toaster } from './components/ui/sonner';

Mapbox.setAccessToken(import.meta.env.VITE_MAPBOX_TOKEN);
Mapbox.addStyleURL({
  Street: 'mapbox://styles/hewitte/cjwaq462118se1cpas6fme42m',
  Satellite: 'mapbox://styles/hewitte/ckekkjxwt0bdk19phn1lb6nnf',
});
Mapbox.setDefaultImages({
  defaultMarker: defaultMarkerImage,
});

const theme = createTheme(
  merge(baseTheme, {
    sizes: {
      appBarHeight: 56,
      sidebarWidth: 272,
      sidebarWidthSm: 74,
      mainWidth: 920,
    },
    layout: {
      main: {
        maxWidth: '$mainWidth',
        marginX: 'auto',
        width: '100%',
      },
      // D19: legacy dialogs centre on the viewport, as the shadcn ones do.
      centeredOverlay: {
        marginLeft: 0,
      },
      centeredOverlayLargeWindow: {
        marginLeft: 0,
      },
    },
  })
);

// D19 and D30: toasts sit above legacy modals (1300) and toasts (1400) and one layer above shadcn's
// dialogs (1450), so a toast raised in an open dialog shows and can be closed; below legacy
// drop-downs (1500). A style prop replaces the generated Toaster's, so its theme variables come along.
const toasterStyle = {
  zIndex: 1460,
  '--normal-bg': 'var(--popover)',
  '--normal-text': 'var(--popover-foreground)',
  '--normal-border': 'var(--border)',
  '--border-radius': 'var(--radius)',
};

const App = () => {
  const auth = useAuthService();

  return (
    <PortalProvider>
      <ThemeProvider theme={theme}>
        <ApiProvider auth={auth}>
          <ProvideAppState>
            <ConfirmProvider>
              <Site>
                <AppRoutes auth={auth} />
              </Site>
              <Toaster style={toasterStyle} />
            </ConfirmProvider>
          </ProvideAppState>
        </ApiProvider>
      </ThemeProvider>
    </PortalProvider>
  );
};

export default App;
